import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Modal, Table, Form, Button, InputGroup } from 'react-bootstrap';
import { useLazyQuery } from '@apollo/client';

import LoadingSpinner from '../../components/LoadingSpinner';
import { COLOR_WHITE, LINEAR_GRADIENT_BG, USER_KEY } from '../../const';
import { FormatNumber } from '../../helper';
import { GET_REWARDS_V2 } from '../../apollo/reward/Query';
import { ReactIcons } from '../../icons';
import { GET_USER_CONFIRM_ONE } from '../../apollo/userconfirm/query';
import { errorSwal } from '../../helper/sweetalert';
import useWindowDimensions from '../../helper/windowDimensions';

const RewardExchangeModal = ({ show, onClose, onSelectReward, userconfirmId }) => {
  const { width} = useWindowDimensions()
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(moment());
  const [selectedUserType, setSelectedUserType] = useState('');
  const [getRewardData, { data: rewardData, loading: rewardLoading }] = useLazyQuery(GET_REWARDS_V2, { fetchPolicy: 'cache-first' });
  const [loadUserConfirm, { data: userConfirmData }] = useLazyQuery(GET_USER_CONFIRM_ONE, { fetchPolicy: 'cache-first' });


  useEffect(() => {
    if (show) {
      getRewardData();
      if (userconfirmId) {
        loadUserConfirm({ variables: { where: { id: userconfirmId } } });
      }
    }
  }, [show]);

  useEffect(() => {
    const t = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(t);
  }, []);

  const userTypeOptions = useMemo(() => {
    const list = (rewardData?.getRewardsV2?.data ?? [])
      .map(r => r?.userType?.name ?? r?.userType ?? '')
      .filter(Boolean);
    return Array.from(new Set(list));
  }, [rewardData]);

  const rewards = useMemo(() => (
    (rewardData?.getRewardsV2?.data ?? [])
      .filter(r => r.status === 'USABLE')
      .filter(r => !search || (r.name || '').toLowerCase().includes(search.toLowerCase()))
      .filter(r => !selectedUserType || ((r?.userType?.name ?? r?.userType) === selectedUserType))
  ), [rewardData, search, selectedUserType]);

  const currentPoint = useMemo(() => (
    userConfirmData?.userconfirmV2?.currentPoint ?? 0
  ), [userConfirmData]);
  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton style={{ background: LINEAR_GRADIENT_BG, color: COLOR_WHITE }}>
        <Modal.Title>ລາຍການລາງວັນ</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#f8f9fa', height: '70vh', overflowY: 'auto' }}>
        <div style={{ backgroundColor: COLOR_WHITE, borderRadius: '10px', padding: width < 480 ? '10px' : '20px', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ display: 'flex',flexDirection: width > 991 ? 'row' : 'column', justifyContent: 'space-between', alignItems: width > 991 ? 'center' : 'flex-start', marginBottom: '15px' }}>
            <h5 style={{ marginBottom: width > 991 ? 0 : '10px' }}>ລາຍການລາງວັນ</h5>
            {/* select reward usertype filter */}
            <div style={{ display: 'flex',justifyContent: width > 991 ? '' : 'between', gap: '10px' }}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="ຄົ້ນຫາລາງວັນ"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Form.Select
                  value={selectedUserType}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                >
                  <option value="">ເລືອກປະເພດລູກຄ້າ</option>
                  {userTypeOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </div>
          </div>

          {rewardLoading ? (
            <LoadingSpinner />
          ) : (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto' }}>
              <Table striped bordered hover responsive="lg" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead style={{ backgroundColor: '#f8f9fa', whiteSpace: 'nowrap' }}>
                  <tr>
                    <th style={{ minWidth: 60 }}>ລຳດັບ</th>
                    <th style={{ minWidth: 120 }}>ຊື່ລາງວັນ</th>
                    <th style={{ minWidth: 80 }}>ປະເພດລາງວັນ</th>
                    <th style={{ minWidth: 80 }}>ປະເພດລູກຄ້າ</th>
                    <th style={{ minWidth: 80 }}>ລາງວັນຍັງເຫຼືອ</th>
                    <th style={{ minWidth: 80 }}>ຄະແນນ</th>
                    <th style={{ minWidth: 120 }}>ໄລຍະເວລາຍັງເຫຼືອ</th>
                    <th style={{ minWidth: 100 }}>ສະຖານະ</th>
                    <th style={{ minWidth: 140 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rewards.map((reward, index) => (
                    <tr key={reward.id ?? index}>
                      <td>{index + 1}</td>
                      <td>{reward.name}</td>
                      <td>{reward.rewardType === 'DISCOUNT' ? 'ສ່ວນຫຼຸດ' : 'ສິນຄ້າ'}</td>
                      <td>{reward.userType?.name}</td>
                      <td>{reward.remainUser}</td>
                      <td>{reward.pointMin} ຄະແນນ</td>
                      <td>
                        {(() => {
                          const end = moment(reward.endDate);
                          const remainingMs = end.diff(now);
                          const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
                          const remainingHours = Math.ceil(remainingMs / (1000 * 60 * 60));

                          if (remainingMs <= 0) {
                            return (
                              <span style={{ color: 'gray' }}>
                                {end.format('DD/MM/YYYY HH:mm')} (0 ວັນ)
                              </span>
                            );
                          }
                          if (remainingMs <= 60 * 60 * 1000) {
                            const hours = Math.floor(remainingMs / (1000 * 60 * 60));
                            const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
                            const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);
                            return (
                              <span>
                                <span style={{ color: 'red' }}>
                                  {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                                </span>
                              </span>
                            );
                          }
                          if (remainingMs < 24 * 60 * 60 * 1000) {
                            return (
                              <>
                                <span style={{ color: 'red' }}> {remainingHours} ຊົ່ວໂມງ</span>
                              </>
                            );
                          }
                          return (
                            <>
                              <span style={{ color: 'red' }}> {remainingDays} ວັນ</span>
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        <span className={`badge ${
                          reward.status === 'USABLE' ? 'bg-success' :
                          reward.status === 'EXPIRE' ? 'bg-warning' :
                          'bg-secondary'
                        }`}>
                          {reward.status === 'USABLE' ? 'ໃຊ້ງານໄດ້' :
                           reward.status === 'EXPIRE' ? 'ໝົດອາຍຸ' :
                           'ປິດໃໍຊ້ງານແລ້ວ'}
                        </span>
                      </td>
                      <td>
                         <Button
                           variant="light"
                           style={{ background: LINEAR_GRADIENT_BG, color: COLOR_WHITE }}
                         onClick={() => {
                         if (!userconfirmId) {
                             errorSwal('ກະລຸນາເລືອກລູກຄ້າກ່ອນ');
                             return;
                           }
                           const userTypeName = userConfirmData?.userconfirmV2?.userType?.name;
                           const rewardUserTypeName = reward?.userType?.name ?? reward?.userType ?? null;
                           const isRewardTypeOpen = rewardUserTypeName === null || rewardUserTypeName === '' || rewardUserTypeName === 'all';
                           if (!isRewardTypeOpen && userTypeName !== rewardUserTypeName) {
                             errorSwal('ລູກຄ້າທ່ານນີ້ບໍ່ມີສິດໃນການແລກລາງວັນນີ້');
                             return;
                         }
                           const needPoint = reward?.pointMin ?? 0;
                           if (currentPoint < needPoint) {
                              errorSwal('ຄະແນນບໍ່ພໍໃນການແລກລາງວັນ');
                              return;
                           }
                           onSelectReward && onSelectReward(reward);
                           onClose();
                          }}
                         >
                           ເລືອກລາງວັນ
                         </Button>
                      </td>
                    </tr>
                ))}

                {rewards.length === 0 && !rewardLoading && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: '#6c757d' }}>
                      ບໍ່ພົບລາງວັນ
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>ປິດ</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RewardExchangeModal;
